/*
 * waitlist (page)
 */
import { appConfig } from "@emr/config"
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Divider,
	Page,
	toast,
} from "@emr/react-ui"
import { AppPlatform } from "@emr/utils"
import { usePushNotification } from "@emr/push-notifications"
import { createFileRoute } from "@tanstack/react-router"
import { Bell, BellRing, CheckCircle, Clock } from "lucide-react"
import { useState } from "react"
import { trpc } from "../trpc"
import { authPageLoaderAuthenticated } from "../utils/auth.utils"

export const Route = createFileRoute("/waitlist")({
	component: WaitlistPage,
	loader: authPageLoaderAuthenticated(appConfig.redirects.afterSignInUrl),
})

function WaitlistPage() {
	const { user } = Route.useLoaderData()
	const [onWaitlist, setOnWaitlist] = useState(
		() => !!user?.user_metadata?.waitlist?.includes(AppPlatform),
	)

	const addToWaitlist = trpc.user.addToWaitlist.useMutation({
		onSuccess: () => {
			setOnWaitlist(true)
			toast.success("You're on the waitlist!")
		},
		onError: (err) => {
			toast.error(err.message)
		},
	})

	const removeFromWaitlist = trpc.user.removeFromWaitlist.useMutation({
		onSuccess: () => {
			setOnWaitlist(false)
			toast.success("You've been removed from the waitlist.")
		},
		onError: (err) => {
			toast.error(err.message)
		},
	})

	return (
		<Page className={"bg-primary/30"}>
			<Card className={"w-full max-w-md p-8 pb-12 text-center"}>
				{onWaitlist ? (
					<>
						<CardHeader className={"items-center gap-3"}>
							<div
								className={
									"w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center"
								}
							>
								<CheckCircle className={"w-7 h-7 text-primary"} />
							</div>
							<CardTitle className={"text-2xl"}>You're on the list!</CardTitle>
							<CardDescription className={"text-base"}>
								Thanks for signing up. We'll reach out when your spot opens up.
							</CardDescription>
						</CardHeader>
						<CardContent className={"flex flex-col gap-4"}>
							<p className={"text-muted-foreground text-sm"}>
								We're working hard to get everything ready. You'll be among the
								first to know when access opens up — we'll reach out to your
								registered email address.
							</p>
							<p className={"text-xs text-muted-foreground"}>
								No spam.{" "}
								<button
									className={
										"underline underline-offset-2 cursor-pointer disabled:opacity-50"
									}
									disabled={removeFromWaitlist.isPending}
									onClick={() => removeFromWaitlist.mutate()}
								>
									{removeFromWaitlist.isPending
										? "Removing..."
										: "Remove me from the waitlist."}
								</button>
							</p>
							<Divider />
							<WaitlistPushOption userId={user.id} />
						</CardContent>
					</>
				) : (
					<>
						<CardHeader className={"items-center gap-3"}>
							<div
								className={
									"w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center"
								}
							>
								<Clock className={"w-7 h-7 text-primary"} />
							</div>
							<CardTitle className={"text-2xl"}>Request Early Access</CardTitle>
							<CardDescription className={"text-base"}>
								We're in early access. Join the waitlist and we'll let you know
								as soon as a spot opens up.
							</CardDescription>
						</CardHeader>
						<CardContent className={"flex flex-col gap-4"}>
							<Button
								className={"w-full"}
								disabled={addToWaitlist.isPending}
								onClick={() => addToWaitlist.mutate()}
							>
								{addToWaitlist.isPending ? "Joining..." : "Join the Waitlist"}
							</Button>
							<p className={"text-xs text-muted-foreground"}>
								No spam. Unsubscribe any time.
							</p>
						</CardContent>
					</>
				)}
			</Card>
		</Page>
	)
}

function WaitlistPushOption({ userId }: { userId: string }) {
	const { isSupported, subscription, subscribe, unsubscribe } =
		usePushNotification({ userId })

	if (!isSupported) {
		return null
	}

	return subscription ? (
		<div className={"flex flex-col items-center gap-2"}>
			<div className={"flex items-center gap-2 text-sm text-primary"}>
				<BellRing className={"w-4 h-4"} />
				<span>{"You'll be notified when approved."}</span>
			</div>
			<button
				className={
					"text-xs text-muted-foreground underline underline-offset-2 cursor-pointer"
				}
				onClick={unsubscribe}
			>
				{"Turn off notifications"}
			</button>
		</div>
	) : (
		<div className={"flex flex-col items-center gap-2"}>
			<p className={"text-sm text-muted-foreground"}>
				{"Want a push notification when you're approved?"}
			</p>
			<Button
				variant={"outline"}
				size={"sm"}
				className={"gap-2"}
				onClick={subscribe}
			>
				<Bell className={"w-4 h-4"} />
				{"Enable Notifications"}
			</Button>
		</div>
	)
}
